<script setup lang="ts">
import { ref, computed } from 'vue'
import { createDiscreteApi } from 'naive-ui'
import { useConversationStore } from '@/stores/conversationStore'
import { formatDate, toInputDate } from '@/utils/format'

const { message } = createDiscreteApi(['message'])
import { useCustomerStore } from '@/stores/customerStore'
import {
  type ChatMessage,
  type ChatTransaction,
  type RunStep,
  type ChatTransactionStatus,
  type UploadedResult,
} from '@/types/conversation.type'
import { CustomerActionTab } from '@/types/customer.type'
import {
  Warning,
  User,
  Document,
  Picture,
  DocumentAdd,
  RefreshRight,
  CloseBold,
  TopRight,
} from '@element-plus/icons-vue'
import AttachmentDialog from '@/components/AttachmentDialog.vue'
import { useRoute, useRouter } from 'vue-router'
import { renderMarkdown } from '@/utils/markdown'

// props
withDefaults(
  defineProps<{
    messages: ChatMessage[]
    activeTx: ChatTransaction | null
    assistantLabel?: string
  }>(),
  { assistantLabel: 'Sales Agent' },
)

// store and dialog state
const conversationStore = useConversationStore()
const customerStore = useCustomerStore()
const route = useRoute()
const isAdminScreen = computed(() => !!route.meta.adminPage)

// Computed
const router = useRouter()
const dialogVisible = ref(false)

const dialogWorkingMemory = ref<string>('')
const dialogRunSteps = ref<RunStep[]>([])
const collapseActive = ref<string[]>([])

// Customer dialog state
const customerDialogVisible = ref(false)
const selectedCustomerNumber = ref<number | null>(null)
const selectedCustomerName = ref<string>('')

// Attachment dialog state
const attachmentDialogVisible = ref(false)
const selectedAttachment = ref<{ attachment: UploadedResult; conversationId: number } | null>(null)

/** 指定されたメッセージの run-steps を取得してモーダル表示 */
async function inspectWorkingMemoryAndSteps(m: ChatMessage) {
  const workingMemory = await conversationStore.getWorkingMemory(
    m.conversationId,
    m.chatTransactionId,
  )
  dialogWorkingMemory.value = workingMemory
  const data = await conversationStore.getRunSteps(m.conversationId, m.chatTransactionId)
  dialogRunSteps.value = data
  collapseActive.value = []
  dialogVisible.value = true
}

function messageFormatter(text: string) {
  return renderMarkdown(text.replace(/\\n/g, '\n'), { breaks: true })
}

/** 顧客情報をクリックした時の処理 */
function handleCustomerClick(accountNumber: number, accountName: string) {
  selectedCustomerNumber.value = accountNumber
  selectedCustomerName.value = accountName
  customerDialogVisible.value = true

  // Clear previous items and fetch new ones
  customerStore.clearExternalCustomerItems()
  customerStore.getExternalCustomerItems(accountNumber)
}

function openCustomerAction(customerId: number) {
  const href = router.resolve({
    name: 'customerAction',
    params: { id: String(customerId) },
    query: { tab: CustomerActionTab.strategy },
  }).href
  window.open(href, '_blank')
}

/** 注文IDをクリックした時の処理 */
function handleOrderClick(orderId: number) {
  if (isAdminScreen.value) {
    router.push(`/admin/order-ticket-list?currentId=${orderId}`)
  } else {
    router.push(`/order-tickets?id=${orderId}`)
  }
}

/** 添付ファイルをクリックした時の処理 */
function handleAttachmentClick(attachment: UploadedResult, conversationId: number) {
  selectedAttachment.value = { attachment, conversationId }
  attachmentDialogVisible.value = true
}

function getSelectedAttachmentUrl(key: string) {
  if (!selectedAttachment.value) throw new Error('Missing: selected attachment.')
  return conversationStore.getFileDownloadUrl(key, selectedAttachment.value.conversationId)
}

/** リトライボタンをクリックした時の処理 */
function handleRetry(userMessage: ChatMessage) {
  if (!isWithinThreeDays(userMessage)) {
    message.error('This message is too old. Please start a new conversation.')
    return
  }

  const RETRY_MESSAGE = 'It appears the process was interrupted. Please try again.'

  // Send retry request message
  // 元メッセージの clientDate は最大 3 日前なので、再送時点の日付で上書きする
  conversationStore.postChatMessage(RETRY_MESSAGE, userMessage.conversationId, undefined, {
    ...userMessage.data,
    clientDate: toInputDate(new Date()),
  })
}

/** エラーメッセージを取得 */
function getErrorMessage(status: ChatTransactionStatus): string {
  if (status === 'failed') return 'An error occurred while answering'
  if (status === 'expired') return 'The answer timed out'
  return ''
}

/** 最新のユーザーメッセージかどうかを判定 */
function isLatestUserMessage(message: ChatMessage, messages: ChatMessage[]): boolean {
  // messagesを逆順で探索して、最初に見つかったユーザーメッセージと一致するか確認
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'user') {
      return messages[i].id === message.id
    }
  }
  return false
}

/** メッセージが3日以内かどうかを判定 */
function isWithinThreeDays(msg: ChatMessage): boolean {
  const threeDaysAgo = new Date()
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3)
  return new Date(msg.createdAt) > threeDaysAgo
}
</script>

<template>
  <div class="messages-container">
    <div v-for="m in messages" :key="m.id" class="message">
      <!-- System Message -->
      <div v-if="m.role == 'system'" class="system-message">
        {{ m.text }}
      </div>

      <!-- User Message -->
      <div v-if="m.role == 'user'" class="user-message-wrapper">
        <div class="user-message">
          <div v-if="isAdminScreen && m.createdAt" class="message-timestamp">
            {{ formatDate(m.createdAt, { withTime: true }) }}
          </div>
          <div
            v-if="m.data?.customerAccountNumber"
            class="customer-reference"
            @click="
              handleCustomerClick(m.data.customerAccountNumber, m.data.customerAccountName || '')
            "
          >
            <el-icon :size="14">
              <User />
            </el-icon>
            <span class="customer-info">
              <span class="customer-number">[{{ m.data.customerAccountNumber }}]</span>
              <span class="customer-name">{{ m.data.customerAccountName }}</span>
            </span>
          </div>
          <div
            v-if="m.data?.attachments && m.data.attachments.length > 0"
            class="attachments-section"
          >
            <div
              v-for="(attachment, index) in m.data.attachments"
              :key="index"
              class="attachment-item"
              @click="handleAttachmentClick(attachment, m.conversationId)"
            >
              <el-icon :size="14">
                <component :is="attachment.fileType === 'image' ? Picture : DocumentAdd" />
              </el-icon>
              <span class="attachment-name">{{ attachment.originalName }}</span>
            </div>
          </div>
          <div class="message-text md-text" v-html="$sanitize(messageFormatter(m.text))" />
          <div
            v-if="m.chatTransaction?.status === 'failed' || m.chatTransaction?.status === 'expired'"
            class="error-section"
          >
            <div class="error-text">
              <Warning />{{ getErrorMessage(m.chatTransaction!.status) }}
            </div>
            <button
              v-if="isLatestUserMessage(m, messages)"
              class="retry-button"
              @click="handleRetry(m)"
            >
              <el-icon :size="14">
                <RefreshRight />
              </el-icon>
              <span>Retry</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Assistant Message -->
      <div v-if="m.role == 'assistant'" class="assistant-message-wrapper">
        <div class="assistant-header">
          <div class="assistant-avatar">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="12" fill="url(#gradient3)" />
              <path
                d="M12 5.5L14.5 8H16V10.5L18.5 13V15.5L16 18H14.5L12 20.5L9.5 18H8L5.5 15.5V13L8 10.5V8H9.5L12 5.5Z"
                fill="white"
                opacity="0.9"
              />
              <circle cx="12" cy="12" r="3" fill="url(#gradient4)" />
              <defs>
                <linearGradient id="gradient3" x1="0" y1="0" x2="24" y2="24">
                  <stop offset="0%" stop-color="#667eea" />
                  <stop offset="100%" stop-color="#764ba2" />
                </linearGradient>
                <linearGradient id="gradient4" x1="8" y1="8" x2="16" y2="16">
                  <stop offset="0%" stop-color="#f093fb" />
                  <stop offset="100%" stop-color="#f5576c" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <span class="assistant-label">{{ assistantLabel }}</span>
        </div>
        <div class="assistant-message">
          <div v-if="isAdminScreen && m.createdAt" class="message-timestamp">
            {{ formatDate(m.createdAt, { withTime: true }) }}
          </div>
          <div
            class="message-text md-text"
            data-testid="assistant-message"
            v-html="$sanitize(messageFormatter(m.text))"
          />

          <div v-if="m.data?.customerPickups?.length" class="customer-pickups">
            <div
              v-for="(pickup, pickupIndex) in m.data.customerPickups"
              :key="pickupIndex"
              class="pickup-group"
            >
              <div class="pickup-title">{{ pickup.title }}</div>
              <button
                v-for="customer in pickup.customers"
                :key="customer.id"
                class="pickup-customer"
                @click="openCustomerAction(customer.id)"
              >
                <span class="pickup-customer-number">[{{ customer.account_number }}]</span>
                <span class="pickup-customer-name">{{ customer.account_name }}</span>
                <el-icon :size="12" class="pickup-open-icon"><TopRight /></el-icon>
              </button>
            </div>
          </div>

          <div v-if="m.data?.outputReport" class="output-report-section">
            <div class="report-stats">
              <div class="stat-item">
                <span class="stat-label">Total:</span>
                <span class="stat-value">{{ m.data.outputReport.order_row_total_count }}</span>
              </div>
              <div class="stat-item success">
                <span class="stat-label">Success:</span>
                <span class="stat-value">{{ m.data.outputReport.order_row_success_count }}</span>
              </div>
              <div class="stat-item todo">
                <span class="stat-label">Review:</span>
                <span class="stat-value">{{ m.data.outputReport.order_row_todo_count }}</span>
              </div>
              <div
                class="stat-item"
                :class="{ ignored: m.data.outputReport.order_row_ignore_count > 0 }"
              >
                <span class="stat-label">Ignored:</span>
                <span class="stat-value">{{ m.data.outputReport.order_row_ignore_count }}</span>
              </div>
            </div>
            <div v-if="m.data.outputReport.help_request_message" class="help-message">
              <div class="help-message-label">Help:</div>
              <div class="help-message-content">{{ m.data.outputReport.help_request_message }}</div>
            </div>
          </div>
          <button
            v-if="m.data?.referencedOrderTicketId"
            class="order-reference-button"
            @click="handleOrderClick(m.data.referencedOrderTicketId)"
          >
            <el-icon :size="14">
              <Document />
            </el-icon>
            <span class="button-text">
              View Order Details
              <span class="order-id">#{{ m.data.referencedOrderTicketId }}</span>
            </span>
          </button>
          <slot name="assistant-extra" :message="m" />
          <div
            v-if="m.chatTransaction?.status === 'failed' || m.chatTransaction?.status === 'expired'"
            class="error-section"
          >
            <div class="error-text">
              <Warning />{{ getErrorMessage(m.chatTransaction!.status) }}
            </div>
          </div>
          <!-- Debug button (開発環境のみ表示) -->
          <button
            v-if="isAdminScreen"
            class="inspect-button"
            @click="inspectWorkingMemoryAndSteps(m)"
          >
            Debug
          </button>
        </div>
      </div>
    </div>

    <!-- Loading Message -->
    <LoadingMessage :active-tx="activeTx" :assistant-label="assistantLabel" />
  </div>

  <!-- Debug Dialog -->
  <el-dialog v-model="dialogVisible" width="95%" :show-close="false" :append-to-body="true">
    <template #header="{ close, titleId, titleClass }">
      <div class="dialog-header">
        <h4 :id="titleId" :class="titleClass">Working Memory & Run Steps</h4>
        <el-button :icon="CloseBold" circle @click="close" />
      </div>
    </template>
    <div class="debug-section">
      <h4>Working Memory</h4>
      <pre v-if="dialogWorkingMemory.length > 0">{{ dialogWorkingMemory }}</pre>
      <pre v-else>Blank</pre>
    </div>
    <div class="debug-section">
      <h4>Run Steps</h4>
      <el-collapse v-model="collapseActive" accordion>
        <el-collapse-item
          v-for="(step, index) in dialogRunSteps"
          :key="index"
          :name="index.toString()"
        >
          <template #title>{{ index + 1 }}: [{{ step.createdAt }}] {{ step.toolName }}</template>
          <div class="debug-content">
            <div class="debug-section">
              <h4>Input</h4>
              <pre>{{ JSON.stringify(step.input, null, 2) }}</pre>
            </div>
            <div class="debug-section">
              <h4>Output</h4>
              <pre>{{ JSON.stringify(step.output, null, 2) }}</pre>
            </div>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>
    <template #footer>
      <el-button @click="dialogVisible = false">Close</el-button>
    </template>
  </el-dialog>

  <!-- Customer Items Dialog -->
  <el-dialog v-model="customerDialogVisible" width="95%" :show-close="false" :append-to-body="true">
    <template #header="{ close, titleId, titleClass }">
      <div class="dialog-header">
        <h4 :id="titleId" :class="titleClass">
          Customer Items: [{{ selectedCustomerNumber }}] {{ selectedCustomerName }}
        </h4>
        <el-button :icon="CloseBold" circle @click="close" />
      </div>
    </template>
    <div class="customer-items-list">
      <div v-if="customerStore.external_customer_items.length > 0" class="items-grid">
        <div v-for="item in customerStore.external_customer_items" :key="item.id" class="item-card">
          <div class="item-title">[{{ item.item_num }}] {{ item.item_name }}</div>
          <div class="item-details-grid">
            <div v-if="item.pack_size" class="detail-item">
              <span class="label">Pack Size</span>
              <span class="value">{{ item.pack_size }}</span>
            </div>
            <div class="detail-item">
              <span class="label">UOM</span>
              <span class="value">{{ item.primary_uom_code }}</span>
            </div>
            <div v-if="item.last_ordered_quantity" class="detail-item">
              <span class="label">Last Ordered Quantity</span>
              <span class="value"
                >{{ item.last_ordered_quantity }} {{ item.last_order_quantity_uom }}</span
              >
            </div>
            <div v-if="item.last_shipped_date" class="detail-item">
              <span class="label">Last Shipped Date</span>
              <span class="value">{{ item.last_shipped_date }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="items-grid">
        <div class="item-card">
          <div class="item-title">No items found</div>
        </div>
      </div>
    </div>
    <template #footer>
      <el-button @click="customerDialogVisible = false">Close</el-button>
    </template>
  </el-dialog>

  <!-- Attachment Dialog -->
  <AttachmentDialog
    :visible="attachmentDialogVisible"
    :attachment="selectedAttachment?.attachment ?? null"
    :get-download-url="getSelectedAttachmentUrl"
    @update:visible="attachmentDialogVisible = $event"
  />
</template>

<style scoped>
.messages-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* System Message */
.system-message {
  text-align: center;
  font-size: var(--font-size-caption);
  color: #999;
  padding: 8px 0;
  margin: 8px 0;
}

/* User Message */
.user-message-wrapper {
  display: flex;
  justify-content: flex-end;
  padding-left: 24px;

  .user-message {
    max-width: 90%;
    background: #409eff;
    color: white;
    border-radius: 18px 18px 4px 18px;
    padding: 4px 14px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

    .message-text {
      font-size: var(--font-size-body);
      line-height: var(--line-height-normal);
      word-wrap: break-word;
    }

    .error-text {
      color: #ffcccc;
    }

    .message-timestamp {
      font-size: 10px; /* タイムスタンプ — 視認性の優先度が低いためトークン不要 */
      color: rgba(255, 255, 255, 0.7);
      margin-bottom: 4px;
    }

    .customer-reference {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: var(--font-size-caption);
      color: rgba(255, 255, 255, 0.85);
      margin-bottom: 6px;
      padding: 3px 8px;
      background: rgba(0, 0, 0, 0.15);
      border-radius: 10px;
      width: fit-content;
      max-width: 100%;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: rgba(0, 0, 0, 0.25);
      }

      &:active {
        transform: scale(0.98);
      }

      .el-icon {
        flex-shrink: 0;
      }

      .customer-info {
        display: flex;
        align-items: center;
        gap: 4px;
        overflow: hidden;

        .customer-number {
          flex-shrink: 0;
          font-weight: 600;
        }

        .customer-name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
        }
      }
    }

    .attachments-section {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 8px;

      .attachment-item {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: var(--font-size-caption);
        color: rgba(255, 255, 255, 0.85);
        padding: 3px 8px;
        background: rgba(0, 0, 0, 0.15);
        border-radius: 10px;
        max-width: 100%;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          background: rgba(0, 0, 0, 0.25);
        }

        &:active {
          transform: scale(0.98);
        }

        .el-icon {
          flex-shrink: 0;
        }

        .attachment-name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 150px;
        }
      }
    }
  }
}

/* Assistant Message */
.assistant-message-wrapper {
  padding-right: 24px;

  .assistant-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
  }

  .assistant-avatar {
    width: 22px;
    height: 22px;
    flex-shrink: 0;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .assistant-label {
    font-size: var(--font-size-caption);
    color: #909399;
    font-weight: 500;
  }

  .assistant-message {
    background: #f0f0f0;
    border-radius: 18px 18px 18px 4px;
    padding: 4px 14px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

    .message-text {
      font-size: var(--font-size-body);
      line-height: var(--line-height-normal);
      color: #333;
      word-wrap: break-word;
    }

    .output-report-section {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid #e0e0e0;

      .report-stats {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-bottom: 6px;

        .stat-item {
          display: flex;
          align-items: center;
          gap: 3px;
          padding: 3px 6px;
          border-radius: 4px;
          border: 1px solid #d0d0d0;
          background: white;
          .stat-label {
            color: #909399;
            font-size: var(--font-size-caption);
          }

          .stat-value {
            font-weight: 600;
            color: #303133;
            font-size: var(--font-size-body);
          }

          &.success {
            border-color: #67c23a;

            .stat-value {
              color: #67c23a;
            }
          }

          &.todo {
            border-color: #e6a23c;

            .stat-value {
              color: #e6a23c;
            }
          }

          &.ignored {
            border-color: #f56c6c;

            .stat-value {
              color: #f56c6c;
            }
          }
        }
      }

      .help-message {
        .help-message-label {
          font-size: var(--font-size-caption);
          color: #909399;
          font-weight: 500;
          margin-bottom: 2px;
        }

        .help-message-content {
          font-size: var(--font-size-body);
          color: #606266;
          line-height: var(--line-height-normal);
          white-space: pre-wrap;
          word-break: break-word;
        }
      }
    }

    .customer-pickups {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 8px;

      .pickup-group {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .pickup-title {
        font-size: var(--font-size-caption);
        font-weight: 600;
        color: #606266;
        margin-bottom: 2px;
      }

      .pickup-customer {
        display: flex;
        align-items: center;
        gap: 4px;
        width: 100%;
        padding: 4px 8px;
        background: white;
        border: 1px solid #ebeef5;
        border-radius: 6px;
        font-family: inherit;
        font-size: var(--font-size-body);
        color: #303133;
        text-align: left;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          background: rgba(64, 158, 255, 0.08);
          border-color: rgba(64, 158, 255, 0.3);
        }

        .pickup-customer-number {
          flex-shrink: 0;
          font-weight: 600;
        }

        .pickup-customer-name {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .pickup-open-icon {
          flex-shrink: 0;
          color: #c0c4cc;
        }
      }
    }

    .order-reference-button {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      margin-top: 6px;
      padding: 4px 10px;
      background: rgba(64, 158, 255, 0.08);
      border: 1px solid rgba(64, 158, 255, 0.2);
      border-radius: 12px;
      font-size: var(--font-size-body);
      color: #409eff;
      cursor: pointer;
      transition: all 0.2s;
      font-family: inherit;

      &:hover {
        background: rgba(64, 158, 255, 0.15);
        border-color: rgba(64, 158, 255, 0.3);
      }

      &:active {
        transform: scale(0.98);
      }

      .el-icon {
        flex-shrink: 0;
      }

      .button-text {
        display: flex;
        align-items: center;
        gap: 3px;

        .order-id {
          font-weight: 600;
        }
      }
    }

    .message-timestamp {
      font-size: 10px; /* タイムスタンプ — 視認性の優先度が低いためトークン不要 */
      color: #909399;
      margin-bottom: 4px;
    }

    .inspect-button {
      margin-top: 8px;
      padding: 4px 12px;
      background: #e0e0e0;
      border: none;
      border-radius: 12px;
      font-size: 12px;
      color: #666;
      cursor: pointer;
      transition: background 0.2s;

      &:hover {
        background: #d0d0d0;
      }
    }
  }
}

/* Error Text */
.error-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  gap: 8px;

  .error-text {
    color: #f56c6c;
    font-size: var(--font-size-caption);
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .retry-button {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 16px;
    color: #666;
    font-size: var(--font-size-caption);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #f5f5f5;
      border-color: #409eff;
      color: #409eff;
    }

    &:active {
      transform: scale(0.95);
    }
  }
}

.error-text {
  color: #f56c6c;
  font-size: var(--font-size-caption);
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 16px;
    height: 16px;
  }
}

/* Debug Dialog */
.debug-section {
  margin-bottom: 16px;

  h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: #666;
  }

  pre {
    background: #f5f5f5;
    padding: 12px;
    border-radius: 4px;
    font-size: 12px;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 300px;
    overflow-y: auto;
  }
}

.debug-content {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .debug-section {
    h4 {
      margin: 0 0 8px 0;
      font-size: 14px;
      color: #666;
    }

    pre {
      background: #f5f5f5;
      padding: 12px;
      border-radius: 4px;
      font-size: 12px;
      overflow-x: auto;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
}

/* Markdown Content Styles */
.md-text {
  :deep(p) {
    margin: 0 0 8px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(ul),
  :deep(ol) {
    margin: 8px 0;
    padding-left: 20px;
  }

  :deep(li) {
    margin: 4px 0;
  }

  :deep(code) {
    background: rgba(0, 0, 0, 0.05);
    padding: 2px 4px;
    border-radius: 3px;
    font-size: 0.9em;
  }

  :deep(pre) {
    background: #f5f5f5;
    padding: 12px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 8px 0;

    code {
      background: none;
      padding: 0;
    }
  }

  :deep(strong) {
    font-weight: 600;
  }

  :deep(a) {
    color: #409eff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.user-message .md-text :deep(code) {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Customer Items Dialog */
.customer-items-list {
  max-height: calc(60dvh - env(safe-area-inset-bottom));
  overflow-y: auto;

  .items-grid {
    display: grid;
    gap: 12px;
  }

  .item-card {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 12px;
    background: white;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .item-title {
      font-size: var(--font-size-body);
      font-weight: 500;
      color: #303133;
      line-height: 1.4;
      margin-bottom: 12px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      line-clamp: 2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .item-details-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;

      .detail-item {
        display: flex;
        flex-direction: column;
        gap: 2px;
        font-size: var(--font-size-caption);

        .label {
          color: #909399;
          font-size: 0.85em;
        }

        .value {
          color: #606266;
          font-weight: 500;
        }
      }
    }
  }
}
</style>

<style>
.md-text img {
  max-width: 100% !important;
  height: auto !important;
  display: block;
  margin: 8px 0;
  border-radius: 8px;
}
</style>
